import React from "react";

import { useNavigate } from "react-router-dom";

import { Footer } from "../../components/Footer";
import { HeaderWrapper } from "../../components/Header/HeaderWrapper";

// ─── Shared primitives ───────────────────────────────────────────────────────

const RED = "#D90000";

const RedDot: React.FC = () => <span className="block w-[8px] h-[8px] rounded-full bg-[#D90000] mb-3" />;

const DotParagraph: React.FC<{ children: React.ReactNode; className?: string; dot?: boolean }> = ({
  children,
  className = "",
  dot = true,
}) => (
  <div className={className}>
    {dot && <RedDot />}
    <p className="text-[14px] md:text-[15px] min-[1442px]:text-[17px] leading-[1.6] text-[#2A2A2A] capitalize">
      {children}
    </p>
  </div>
);

const SectionTitle: React.FC<{ plain: string; red: string; className?: string }> = ({ plain, red, className = "" }) => (
  <h2 className={`text-center text-[24px] md:text-[30px] min-[1442px]:text-[36px] font-normal text-[#2A2A2A] leading-tight ${className}`}>
    {plain} <span style={{ color: RED }}>{red}</span>
  </h2>
);

const container = "w-full max-w-[1240px] min-[1442px]:max-w-[1500px] mx-auto px-4 md:px-8";

// ─── Copy ────────────────────────────────────────────────────────────────────

const P_EXPENSIVE =
  "Things are expensive right now, and we all want our dollars to go further. We want to enjoy our weekends, make memories with our families, and support the local shops that make our neighborhoods great.";
const P_DIVIDE =
  "But there is a growing divide. Massive corporate companies have the budgets for sophisticated marketing tech, while local businesses are left struggling to afford basic advertising.";
const P_IDEA =
  "The idea for My Couponbook came from seeing this first-hand. Our founder ran a local mechanic shop for 10 years and realized a hard truth: expensive marketing eats a business's profits, and ultimately, the consumer ends up paying for those ads through higher prices.";
const P_MECHANIC =
  "When that mechanic shop cut out traditional marketing and passed those savings directly to customers through special discounts, it created a viral, word-of-mouth community.";

const P_THOUGHT =
  "We thought back to the classic $20 paper coupon books sold door-to-door to support local causes. We decided to digitize that exact concept. We built an all-in-one coupon and rewards app to drastically cut the cost of marketing for local businesses, empowering them to offer incredible deals so you can keep your hard-earned dollars and experience more of your city.";


// ─── Local Heart, National Support ─────────────────────────────────────────────────

const P_MISSION =
  "Our core mission is entirely dedicated to helping local Canadian businesses thrive.";

const P_SUPPORT =
  "When you support local, you create a ripple effect. Local businesses hire within the community, they buy from local suppliers, and they pay taxes directly to your municipality. Supporting local helps everyone around you grow.";

const P_APP =
  "You will also notice top national brands featured on our app. We partner with these large brands to provide premium promo codes because it generates the income necessary to keep our core platform completely free and highly affordable for local businesses.";

const P_ECOSYSTEM =
  "It’s an ecosystem where everyone wins: you get access to Canada's best discounts in one place, and local shops get a world-class marketing platform without the corporate price tag.";

// ─── Icons ───────────────────────────────────────────────────────────────────

const Svg: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = "w-[22px] h-[22px]" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    {children}
  </svg>
);

const TicketsIcon = () => (
  <Svg>
    <path d="M3.5 9.2 9.2 3.5a1.5 1.5 0 0 1 2.1 0l1.2 1.2a2 2 0 0 0 2.8 2.8l1.2 1.2a1.5 1.5 0 0 1 0 2.1l-5.7 5.7a1.5 1.5 0 0 1-2.1 0l-1.2-1.2a2 2 0 0 0-2.8-2.8l-1.2-1.2a1.5 1.5 0 0 1 0-2.1Z" />
    <path d="m13.3 17.6 1.8-1.8 1.2 1.2a1.5 1.5 0 0 1 0 2.1l-.9.9a1.5 1.5 0 0 1-2.1 0l-.9-.9 .9-1.5Zm4.3-4.3 1.8-1.8 .9.9a1.5 1.5 0 0 1 0 2.1l-.9.9-1.8-1.1Z" />
  </Svg>
);
const StoreIcon = () => (
  <Svg>
    <path
      fillRule="evenodd"
      d="M9.5 3h5A1.5 1.5 0 0 1 16 4.500V6h4a2 2 0 0 1 2 2v3H2V8a2 2 0 0 1 2-2h4V4.500A1.500 1.500 0 0 1 9.500 3Zm.5 3h4V5h-4v1Z"
    />
    <path d="M2 12.5h7.500V14a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-1.500H22V19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-6.500Zm9 0h2v1h-2v-1Z" />
  </Svg>
);
const PhoneIcon = () => (
  <Svg>
    <path d="M7 2h10a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2Zm4 17a1 1 0 1 0 2 0 1 1 0 0 0-2 0Z" />
  </Svg>
);
const LaunchIcon = () => (
  <Svg>
    <path
      fillRule="evenodd"
      d="M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Zm-.500 4.750v.900h15v-.900h-15Zm1.500-3a.750.750 0 1 0 0 1.500.750.750 0 0 0 0-1.500Zm2.500 0a.750.750 0 1 0 0 1.500.750.750 0 0 0 0-1.500Zm2.500 0a.750.750 0 1 0 0 1.500.750.750 0 0 0 0-1.500Z"
    />
  </Svg>
);
const MapleIcon = () => (
  <Svg>
    <path d="m12 2 1.7 3.4 1.8-.9-.6 4.4 2.6-2.7.7 1.6 2.8-.6-1 3 1.5.8-4.6 3.8.5 1.6-4.4-.8V21h-2v-5.4l-4.4.8.5-1.6-4.6-3.8 1.5-.8-1-3 2.8.6.7-1.6 2.6 2.7-.6-4.4 1.8.9L12 2Z" />
  </Svg>
);
const SparkleIcon = () => (
  <Svg className="w-[16px] h-[16px]">
    <path d="M12 2c.6 5.6 4.4 9.4 10 10-5.6.6-9.4 4.4-10 10-.6-5.6-4.4-9.4-10-10 5.6-.6 9.4-4.4 10-10Z" />
  </Svg>
);
const StampIcon = () => (
  <Svg className="w-[16px] h-[16px]">
    <path d="M12 3a3 3 0 0 1 2.300 4.900c-.5.700-.8 1.300-.8 2.100v1H17a2 2 0 0 1 2 2v3H5v-3a2 2 0 0 1 2-2h3.500v-1c0-.8-.3-1.400-.8-2.100A3 3 0 0 1 12 3ZM5 18h14v3H5v-3Z" />
  </Svg>
);
const GiftIcon = () => (
  <Svg className="w-[16px] h-[16px]">
    <path d="M4 9h16v3H4zM5 13h6v8H5zM13 13h6v8h-6zM11 9h2v12h-2zM12 8c-1-2-2.5-4-4.2-3.4C6.5 5.1 7 7.5 9 8h3Zm0 0c1-2 2.5-4 4.2-3.4 1.3.5.8 2.9-1.2 3.4h-3Z" />
  </Svg>
);
const MegaphoneIcon = () => (
  <Svg className="w-[16px] h-[16px]">
    <path d="M3 10v4a1 1 0 0 0 1 1h2l4 4V5L6 9H4a1 1 0 0 0-1 1Zm9-4.5v13l8 3v-19l-8 3Z" />
  </Svg>
);
const HeartIcon = () => (
  <Svg className="w-[16px] h-[16px]">
    <path d="M12 21s-7.5-4.6-9.4-9.3C1.2 8.2 3.3 4.5 7 4.5c2 0 3.4 1.1 5 3 1.6-1.9 3-3 5-3 3.7 0 5.8 3.7 4.4 7.2C19.5 16.4 12 21 12 21Z" />
  </Svg>
);
const MobileIcon = () => (
  <Svg className="w-[16px] h-[16px]">
    <path d="M8 2h8a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2Zm3 17a1 1 0 1 0 2 0 1 1 0 0 0-2 0Z" />
  </Svg>
);
const HandshakeIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 64 64" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M2 22 14 10l8 8-12 12L2 22Zm60 0L50 10l-8 8 12 12 8-8Z" />
    <path d="M24 18h10l6-4 12 12-4 4-12-10-8 6c-2 1.5-5 1-6-1s0-4 2-5Z" />
    <path d="m12 32 4-4 6 6c1.5 1.5 4 1.5 5.5 0l8-6 12 12c1.5 1.5 1.5 3.7 0 5.2-1.4 1.4-3.6 1.4-5 0l-4-4 3 3c1.5 1.5 1.5 3.7 0 5.2-1.4 1.4-3.6 1.4-5 0l-3-3 2 2c1.5 1.5 1.5 3.7 0 5.2-1.4 1.4-3.6 1.4-5 0L12 32Z" />
  </svg>
);
const BriefcaseIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      d="M9.5 3h5A1.5 1.5 0 0 1 16 4.5V6h4a2 2 0 0 1 2 2v3H2V8a2 2 0 0 1 2-2h4V4.5A1.5 1.5 0 0 1 9.5 3Zm.5 3h4V5h-4v1Z"
    />
    <path d="M2 12.5h7.5V14a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-1.5H22V19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-6.5Zm9 0h2v1h-2v-1Z" />
  </svg>
);

// ─── 1. Hero ─────────────────────────────────────────────────────────────────

const glassCard: React.CSSProperties = {
  backgroundColor: "rgba(255, 231, 231, 0.4)",
  WebkitBackdropFilter: "blur(5px)",
  backdropFilter: "blur(5px)",
};

const HeroSection: React.FC = () => (
  <section className="relative w-full overflow-hidden">
    {/* Halifax waterfront backdrop */}
    <img
      src="/images/Webp/AboutHeroBG.webp"
      alt=""
      className="absolute inset-0 w-full h-full object-cover object-[25%_bottom] lg:object-bottom"
    />

    <div className={`${container} relative grid grid-cols-1 lg:grid-cols-2 items-center gap-10 pt-[120px] md:pt-[140px] pb-12 lg:pb-0 lg:min-h-[640px] min-[1442px]:min-h-[760px]`}>
      <div className="text-left">
        <h1 className="text-[36px] md:text-[48px] min-[1200px]:text-[56px] min-[1442px]:text-[68px] leading-[1.15] font-normal text-[#1A1A1A] tracking-[-0.5px]">
          Canada&apos;s Best
          <br />
          Coupon App for
          <br />
          <span className="font-semibold" style={{ color: RED }}>
            Local Businesses
          </span>
        </h1>
        <p className="mt-4 max-w-[440px] text-[15px] md:text-[17px] min-[1442px]:text-[19px] leading-[1.5] text-[#444]">
          Discover top deals, exclusive promo codes, and get rewarded for supporting your local community.
        </p>
      </div>

      <div className="relative flex justify-center lg:justify-end h-[360px] md:h-[460px] lg:h-[560px] min-[1442px]:h-[680px]">
        <img
          src="/images/Webp/AboutHeroMockup.webp"
          alt="My Couponbook app"
          className="h-full w-auto object-contain"
        />

        {/* Floating cards — same glass style as the landing page "Personalised for You" section */}
        {/* Zero Commissions card */}
        <div
          style={{ ...glassCard, border: "4px solid #FFFFFF", boxShadow: "0px 40px 80px rgba(0,0,0,0.25)" }}
          className="about-float absolute left-[0%] md:left-[8%] lg:left-[-4%] top-[14%] w-[140px] md:w-[180px] min-[1442px]:w-[210px] rounded-[20px] md:rounded-[26px] overflow-hidden z-30 text-left"
        >
          <div
            className="m-[4px] rounded-[16px] md:rounded-[22px] bg-cover bg-center text-white p-[12px] md:p-[16px]"
            style={{ backgroundImage: "url('/images/Webp/PersonalisedforYouRedCardBG.webp')" }}
          >
            <div className="text-[18px] md:text-[24px] min-[1442px]:text-[28px] leading-none font-semibold mb-1">Zero</div>
            <div className="text-[10px] md:text-[12px] min-[1442px]:text-[14px] font-normal opacity-90">Commissions</div>
          </div>
          <div className="px-[12px] md:px-[16px] py-[8px] md:py-[10px]">
            <div className="text-[11px] md:text-[13px] min-[1442px]:text-[15px] font-semibold text-black">Business Owner</div>
          </div>
        </div>

        {/* Premium card */}
        <div
          style={{ ...glassCard, border: "4px solid #FFFFFF", boxShadow: "0px 40px 80px rgba(0,0,0,0.18)" }}
          className="about-float-reverse absolute right-[-2%] lg:right-[-4%] top-[8%] flex items-center gap-[10px] md:gap-[12px] px-[12px] md:px-[16px] py-[10px] md:py-[12px] rounded-[18px] md:rounded-[22px] z-20 text-left"
        >
          <span
            style={{ border: "2px solid #FFFFFF" }}
            className="w-[36px] h-[36px] md:w-[46px] md:h-[46px] shrink-0 rounded-[8px] md:rounded-[12px] bg-gradient-to-r from-red-600 to-red-900 text-white flex items-center justify-center [&>svg]:w-[18px] [&>svg]:h-[18px] md:[&>svg]:w-[22px] md:[&>svg]:h-[22px]"
          >
            <HeartIcon />
          </span>
          <div>
            <div className="text-[11px] md:text-[13px] min-[1442px]:text-[15px] font-semibold text-black">Premium Members</div>
            <div className="text-[9px] md:text-[10px] min-[1442px]:text-[12px] font-normal leading-snug text-gray-600 max-w-[110px] md:max-w-[130px]">
              Save 20% OFF every deal
            </div>
          </div>
        </div>

        {/* Verified deal card */}
        <div
          style={{ ...glassCard, border: "4px solid #FFFFFF", boxShadow: "0px 40px 80px rgba(0,0,0,0.18)" }}
          className="about-float-reverse absolute left-[-2%] md:left-[2%] lg:left-[-10%] bottom-[10%] flex items-center gap-[10px] md:gap-[12px] px-[12px] md:px-[16px] py-[10px] md:py-[12px] rounded-[18px] md:rounded-[22px] z-20 text-left"
        >
          <img
            src="/images/Webp/JonBowman.webp"
            alt=""
            style={{ border: "2px solid #FFFFFF" }}
            className="w-[36px] h-[36px] md:w-[46px] md:h-[46px] rounded-[8px] md:rounded-[12px] object-cover shrink-0"
          />
          <div>
            <div className="text-[11px] md:text-[13px] min-[1442px]:text-[15px] font-semibold text-black">Verified Deal</div>
            <div className="text-[9px] md:text-[10px] min-[1442px]:text-[12px] font-normal leading-snug text-gray-600 max-w-[130px] md:max-w-[160px]">
              Trusted local businesses offering genuine savings
            </div>
          </div>
        </div>

        <style>{`
          @keyframes aboutFloat { 0%,100% { transform: translateY(0px); } 50% { transform: translateY(-16px); } }
          @keyframes aboutFloatReverse { 0%,100% { transform: translateY(-10px); } 50% { transform: translateY(10px); } }
          .about-float { animation: aboutFloat 6s ease-in-out infinite; }
          .about-float-reverse { animation: aboutFloatReverse 7s ease-in-out infinite; }
        `}</style>
      </div>
    </div>
  </section>
);

// ─── 2. Building a better way to save ────────────────────────────────────────

const BuildingSection: React.FC = () => (
  <section className={`${container} pt-16 md:pt-24`}>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-10">
      <h2 className="md:self-center text-[34px] md:text-[42px] min-[1442px]:text-[52px] leading-[1.15] font-normal text-[#2A2A2A]">
        Building a
        <br />
        <span className="font-semibold" style={{ color: RED }}>
          Better Way to Save
        </span>
      </h2>
      <div className="flex flex-col gap-6">
        <DotParagraph dot={false}>{P_EXPENSIVE}</DotParagraph>
        <DotParagraph dot={false}>{P_DIVIDE}</DotParagraph>
        <DotParagraph dot={false}>{P_IDEA}</DotParagraph>
      </div>
    </div>
  </section>
);

// ─── 3. Café image + story ───────────────────────────────────────────────────

const StoryImageSection: React.FC = () => (
  <section className={`${container} pt-12 md:pt-16`}>
    <div className="rounded-[24px] md:rounded-[30px] overflow-hidden aspect-[16/10] md:aspect-[2/1]">
      <img
        src="/images/Webp/CoffeeShopBG.webp"
        alt="A busy local café"
        className="w-full h-full object-cover [filter:brightness(1.9)_saturate(1.1)]"
      />
    </div>
    <DotParagraph dot={false} className="mt-10">
      {P_MECHANIC} {P_THOUGHT}
    </DotParagraph>
  </section>
);

// ─── 4. From paper coupons to digital rewards ────────────────────────────────

const MILESTONES = [
  { Icon: TicketsIcon, label: "2000s\nCommunity\nCoupons" },
  { Icon: StoreIcon, label: "Local Business\nSuccess" },
  { Icon: PhoneIcon, label: "Digital\nTransformation" },
  { Icon: LaunchIcon, label: "My Couponbook\nLaunch" },
  { Icon: MapleIcon, label: "Canada-Wide\nSavings\nPlatform" },
];

const MilestonesSection: React.FC = () => (
  <section className={`${container} pt-16 md:pt-20`}>
    <div className="rounded-[24px] md:rounded-[30px] bg-[#FFF5CF] px-4 md:px-8 py-10 md:py-14">
      <SectionTitle plain="From Paper Coupons to" red="Digital Rewards" />
      <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6 lg:gap-9">
        {MILESTONES.map(({ Icon, label }, i) => (
          <div
            key={label}
            style={{ border: "1px solid #3A3A3A" }}
            className={`rounded-[14px] bg-transparent min-h-[140px] md:min-h-[164px] flex flex-col items-center justify-center gap-3 px-3 text-center text-[#2A2A2A] [&>svg]:w-[34px] [&>svg]:h-[34px] ${
              i === MILESTONES.length - 1 ? "col-span-2 sm:col-span-1" : ""
            }`}
          >
            <Icon />
            <span className="font-['Poppins',Helvetica,sans-serif] text-[14px] md:text-[16px] min-[1442px]:text-[17px] font-medium leading-[1.45] text-[#1A1A1A] whitespace-pre-line">{label}</span>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ─── 5. Local heart, national support ────────────────────────────────────────

const LocalHeartSection: React.FC = () => (
  <section className={`${container} pt-16 md:pt-24`}>
    <SectionTitle plain="Local Heart," red="National Support" />
    <div className="mt-10 md:mt-14 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-12 items-center">
      <div className="flex flex-col gap-8">
        <DotParagraph>{P_MISSION}</DotParagraph>
        <DotParagraph>{P_SUPPORT}</DotParagraph>
        <DotParagraph>{P_APP}</DotParagraph>
        <DotParagraph>{P_ECOSYSTEM}</DotParagraph>
      </div>

      <div className="relative w-full max-w-[560px] mx-auto lg:ml-auto lg:mr-0 rounded-[24px] md:rounded-[30px] overflow-hidden aspect-[852/1032]">
        <img
          src="/images/Webp/AboutLocalHeart.webp"
          alt="My Couponbook business dashboard"
          className="absolute inset-0 w-full h-full object-cover"
        />

        {/* Multi coupons send card */}
        <div
          style={{ border: "1px solid rgba(255,255,255,0.6)" }}
          className="absolute right-[7%] top-[5%] w-[62%] md:w-[47%] rounded-[16px] bg-white/50 backdrop-blur-md shadow-[0_14px_40px_rgba(0,0,0,0.15)] p-2.5"
        >
          <div className="flex items-center gap-2 px-1 pb-2">
            <span className="w-[18px] h-[18px] rounded-full bg-[#D90000] text-white text-[10px] flex items-center justify-center">✓</span>
            <span className="text-[11px] md:text-[12px] font-medium text-[#222]">Multi Coupons Send</span>
          </div>
          <div className="rounded-[12px] bg-gradient-to-br from-[#ef0023] to-[#b8001a] text-white px-3 py-2.5">
            <div className="text-[11px] md:text-[12px] opacity-90">Send Coupons To</div>
            <div className="flex items-end justify-between">
              <div>
                <div className="text-[22px] md:text-[26px] leading-none mt-1">893</div>
                <div className="text-[10px] md:text-[11px] opacity-90 mt-1">Your Order</div>
              </div>
              <span className="bg-white text-[#D90000] text-[10px] md:text-[11px] font-semibold rounded-full px-4 py-1">Send</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ─── 6. What it means to launch with us ──────────────────────────────────────

type Perk = { Icon: React.FC; text: string };

const COMMUNITY_PERKS: Perk[] = [
  { Icon: SparkleIcon, text: "Experience more: find the best Canadian deals and stretch your weekend budget." },
  { Icon: GiftIcon, text: "Earn rewards: get Coupon Rewards every time you support the businesses you love." },
  { Icon: StampIcon, text: "Fund your next adventure: every dollar saved is a dollar you can put toward your next great experience." },
];

const BUSINESS_PERKS: Perk[] = [
  { Icon: MegaphoneIcon, text: "Ditch expensive ads: pass your marketing savings directly to your customers." },
  { Icon: HeartIcon, text: "Build real loyalty: turn passing foot traffic into regulars." },
  { Icon: MobileIcon, text: "Connect directly: when a user follows you, you gain a direct channel to bring them back with targeted deals and push notifications." },
];

const poppins = "font-['Poppins',Helvetica,sans-serif]";

const PerkCard: React.FC<{ title: string; image: string; perks: Perk[]; reverse?: boolean }> = ({ title, image, perks, reverse }) => (
  <div className={`flex flex-col sm:flex-row ${reverse ? "sm:flex-row-reverse" : ""} rounded-[30px] overflow-hidden bg-[#D4D4D4] sm:min-h-[370px] ${poppins}`}>
    <img src={image} alt="" className="w-full sm:w-[47.5%] h-[260px] sm:h-auto object-cover object-top" />
    <div className="flex-1 px-6 py-8 flex flex-col justify-center">
      <h3 className={`text-[20px] min-[1442px]:text-[22px] font-semibold text-[#1A1A1A] mb-8 ${reverse ? "sm:text-right" : ""}`}>
        {title}
      </h3>
      <ul className="flex flex-col gap-8 list-none p-0 m-0">
        {perks.map(({ Icon, text }) => (
          <li key={text} className={`flex items-center gap-3.5 ${reverse ? "sm:flex-row-reverse sm:text-right" : ""}`}>
            <span
              style={{ border: "1px solid #2A2A2A" }}
              className="w-[38px] h-[38px] shrink-0 rounded-[9px] bg-[#E3E3E3] text-[#2A2A2A] flex items-center justify-center [&>svg]:w-[20px] [&>svg]:h-[20px]"
            >
              <Icon />
            </span>
            <span className="text-[13px] min-[1442px]:text-[14px] font-medium leading-[1.45] text-[#1A1A1A] capitalize">{text}</span>
          </li>
        ))}
      </ul>
    </div>
  </div>
);

const LaunchWithUsSection: React.FC = () => (
  <section className={`${container} pt-16 md:pt-28 pb-16 md:pb-24 ${poppins}`}>
    <h2 className="text-center text-[28px] md:text-[36px] min-[1442px]:text-[42px] font-normal leading-tight tracking-[-0.5px] text-[#3D3D3D]">
      What it means to <span style={{ color: RED }}>launch with us.</span>
    </h2>
    <p className="mt-5 text-center text-[14px] md:text-[17px] font-medium leading-[1.45] text-[#1A1A1A] max-w-[840px] mx-auto capitalize">
      When you become a founding partner, we equip you with a complete growth engine to ensure your success from day one.
    </p>
    <div className="mt-12 md:mt-24 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 xl:gap-20">
      <PerkCard title="For The Community" image="/images/Webp/AboutLaunchCommunity.webp" perks={COMMUNITY_PERKS} />
      <PerkCard title="For Local Businesses" image="/images/Webp/AboutLaunchBusiness.webp" perks={BUSINESS_PERKS} reverse />
    </div>
  </section>
);

// ─── 7. Split CTA ────────────────────────────────────────────────────────────

const ctaHeading =
  "text-[44px] sm:text-[52px] lg:text-[64px] min-[1200px]:text-[78px] min-[1442px]:text-[88px] leading-[1.12] font-light tracking-[-1px] m-0";
const ctaButton =
  "mt-8 lg:mt-10 inline-flex items-center justify-center h-[46px] lg:h-[52px] px-7 lg:px-9 rounded-full text-[13px] lg:text-[15px] font-medium no-underline transition-colors";

const CtaSection: React.FC = () => {
  const navigate = useNavigate();
  return (
    <section className={`w-full bg-[#F7F0EE] py-10 md:py-14 ${poppins}`}>
      <div className={container}>
        <div
          style={{ border: "1px solid #1A1A1A" }}
          className="grid grid-cols-1 md:grid-cols-2 rounded-[24px] md:rounded-[30px] overflow-hidden"
        >
          {/* Customer */}
          <div className="flex flex-col items-center md:items-end justify-center max-md:text-center md:text-right px-8 md:px-10 lg:px-14 py-12 md:py-14 md:min-h-[380px] min-[1200px]:min-h-[450px]">
            <h2 className={`${ctaHeading} text-[#3D3D3D] max-md:text-center md:text-right`}>
              Ready to
              <br />
              Experience
              <br />
              <span className="inline-flex items-center gap-2">
                <HandshakeIcon className="w-[0.85em] h-[0.85em]" />
                More?
              </span>
            </h2>
            <a href="/landing#download" className={`${ctaButton} border-[1px] border-solid border-[#3D3D3D] bg-[#3D3D3D] text-[#FFFFFF] hover:bg-transparent hover:text-[#3D3D3D]`}>
              Download The App Today
            </a>
          </div>

          {/* Business */}
          <div className="bg-[#3D3D3D] flex flex-col items-center md:items-start justify-center max-md:text-center md:text-left px-8 md:px-10 lg:px-14 py-12 md:py-14 md:min-h-[380px] min-[1200px]:min-h-[450px]">
            <h2 className={`${ctaHeading} text-white max-md:text-center md:text-left`}>
              Own a
              <br />
              <span className="inline-flex items-center gap-3">
                Local
                <BriefcaseIcon className="w-[0.7em] h-[0.7em]" />
              </span>
              <br />
              Business?
            </h2>
            <button
              type="button"
              onClick={() => navigate("/business")}
              className={`${ctaButton} border-[1px] border-solid border-white bg-[transparent] text-[#FFFFFF] hover:bg-white hover:text-[#3D3D3D]`}
            >
              Start Growing For Free
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

// ─── Page ────────────────────────────────────────────────────────────────────

const About: React.FC = () => (
  <div className="relative w-full bg-white overflow-x-hidden min-h-screen">
    <HeaderWrapper />
    <main className="relative z-10 flex flex-col">
      <HeroSection />
      <BuildingSection />
      <StoryImageSection />
      <MilestonesSection />
      <LocalHeartSection />
      <LaunchWithUsSection />
      <CtaSection />
    </main>
    <Footer />
  </div>
);

export default About;
